import type { ReactNode } from "react";

/** Shared presentation used by the approved demos and real payroll screens. */
export function WorkflowStepBadge({
  active,
  total,
  name,
}: {
  active: number;
  total: number;
  name: string;
}) {
  return (
    <span className="inline-flex min-h-8 items-center gap-2 rounded-lg border border-border bg-accent-subtle px-2.5 py-1 text-xs font-medium text-accent">
      Step {active} of {total} · {name}
    </span>
  );
}

export interface CompactTableColumn {
  label: string;
  align?: "left" | "right" | "center";
}

/** Native, read-only table shell; interactive grids use the DataTable adapter. */
export function CompactTable({
  headers,
  children,
  label,
}: {
  headers: CompactTableColumn[];
  children: ReactNode;
  label?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-background">
      <table aria-label={label} className="w-full text-sm">
        <thead>
          <tr className="bg-surface/60 text-xs font-semibold text-foreground-subtle">
            {headers.map((header) => (
              <th
                key={header.label}
                scope="col"
                className={`px-4 py-2 font-semibold ${
                  header.align === "right"
                    ? "text-right"
                    : header.align === "center"
                      ? "text-center"
                      : "text-left"
                }`}
              >
                {header.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">{children}</tbody>
      </table>
    </div>
  );
}

export function EmployeeIdentity({
  name,
  subtitle,
}: {
  name: string;
  subtitle?: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span
        aria-hidden="true"
        className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border-strong bg-surface text-xs font-semibold text-foreground"
      >
        {name
          .trim()
          .split(/\s+/)
          .slice(0, 2)
          .map((part) => part[0])
          .join("")}
      </span>
      <div className="min-w-0">
        <p className="truncate font-medium text-foreground">{name}</p>
        {subtitle && (
          <p className="mt-0.5 truncate text-xs text-foreground-muted">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
